// utils/StorageUtil.js
const STORAGE_KEY_PREFIX = "rotiey_"; // Namespace for keys
const DEFAULT_EXPIRY_HOURS = 48; // Default expiration: 48 hours
const LOCATION_DATA_KEY = "userLocation"; // Specific key for location data
const LOCATION_DATA_EXPIRY_HOURS = 48; // Location data expires after 24 hours

// Helper to get current timestamp in milliseconds
const getCurrentTimestamp = () => Date.now();

// Helper to calculate expiry timestamp
const getExpiryTimestamp = (hours) => getCurrentTimestamp() + hours * 60 * 60 * 1000;

// Set item in localStorage with expiration
export const setItem = (key, value, expiryHours = DEFAULT_EXPIRY_HOURS) => {
  try {
    const item = {
      value: value,
      expiry: getExpiryTimestamp(expiryHours),
    };
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${key}`, JSON.stringify(item));
  } catch (error) {
    console.error(`Error setting localStorage item ${key}:`, error);
  }
};

// Set locationData with 24-hour expiration
export const setLocationData = (value) => {
  setItem(LOCATION_DATA_KEY, value, LOCATION_DATA_EXPIRY_HOURS);
};

// Get item from localStorage and check expiration
export const getItem = (key) => {
  try {
    const itemStr = localStorage.getItem(`${STORAGE_KEY_PREFIX}${key}`);
    if (!itemStr) return null;

    const item = JSON.parse(itemStr);
    const now = getCurrentTimestamp();

    // Check if item has expired
    if (now > item.expiry) {
      localStorage.removeItem(`${STORAGE_KEY_PREFIX}${key}`);
      return null;
    }

    return item.value;
  } catch (error) {
    console.error(`Error getting localStorage item ${key}:`, error);
    return null;
  }
};

// Get locationData and check its specific expiration
export const getLocationData = () => {
  return getItem(LOCATION_DATA_KEY);
};

// Remove item from localStorage
export const removeItem = (key) => {
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${key}`);
  } catch (error) {
    console.error(`Error removing localStorage item ${key}:`, error);
  }
};

// Remove locationData specifically
export const removeLocationData = () => {
  removeItem(LOCATION_DATA_KEY);
};

// Clear all expired items
export const clearExpiredItems = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(STORAGE_KEY_PREFIX))
      .forEach((key) => {
        const itemStr = localStorage.getItem(key);
        if (!itemStr) return;

        const item = JSON.parse(itemStr);
        if (getCurrentTimestamp() > item.expiry) {
          localStorage.removeItem(key);
        }
      });
  } catch (error) {
    console.error("Error clearing expired localStorage items:", error);
  }
};

// Component to initialize storage cleanup
const StorageUtil = () => {
  clearExpiredItems(); // Run cleanup on render
  return null; // No UI, just a utility component
};

export default StorageUtil; // Export as default for use as <StorageUtil />